import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { postsApi } from '../services/api';
import hiveService from '../services/hive';
import { usePostCache } from '../context/PostCacheContext';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import ArticleHeader from '../components/ArticleHeader/ArticleHeader';
import ArticleContent from '../components/ArticleContent/ArticleContent';
import ReadingProgressBar from '../components/ReadingProgressBar/ReadingProgressBar';
import ArticleNavButtons from '../components/ArticleNavButtons/ArticleNavButtons';
import ArticleActions from '../components/ArticleActions/ArticleActions';
import CommentsSection from '../components/CommentsSection/CommentsSection';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import { buildOptimisticCommentUpsertPayload } from '../utils/optimisticComment';
import { buildPostPath, buildContentPath } from '../utils/postUrls';
import { getPostAppInfo } from '../utils/postApp';
import { buildCommentTree, flattenCommentTree } from '../utils/commentTree';
import { useActiveEditor } from '../context/ActiveEditorContext';
import { useAuth } from '../context/AuthContext';
import { HIVE_FRONTENDS } from '../config/hiveFrontends';
import './PostDetail.css';

const normalizeNestedComments = (nodes = [], rootAuthor, rootPermlink, seen = new Set()) => {
  if (!Array.isArray(nodes)) return [];

  const out = [];
  for (const node of nodes) {
    const nodeAuthor = node?.author || '';
    const nodePermlink = node?.permlink || '';
    if (!nodeAuthor || !nodePermlink) continue;

    const key = `${nodeAuthor}/${nodePermlink}`;
    if (key === `${rootAuthor}/${rootPermlink}`) continue;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({
      ...node,
      replies: normalizeNestedComments(node?.replies || [], rootAuthor, rootPermlink, seen)
    });
  }

  return out;
};

const updateInTree = (nodes, permlink, updates) =>
  nodes.map(node =>
    node.permlink === permlink
      ? { ...node, ...updates }
      : { ...node, replies: updateInTree(node.replies || [], permlink, updates) }
  );

const PostDetail = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { author: rawAuthor, permlink } = useParams();
  const author = rawAuthor?.startsWith('@') ? rawAuthor.slice(1) : rawAuthor;
  const { openEditor } = useActiveEditor();
  const replyEditorId = `article-reply:${author}/${permlink}`;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFromCache, addToCache } = usePostCache();
  const [post, setPost] = useState(null);
  const [parentPost, setParentPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [readingTime, setReadingTime] = useState(0);
  const articleRef = useRef(null);
  const commentsRef = useRef(null);
  const replyBoxRef = useRef(null);
  const [isDistractionlessMode, setIsDistractionlessMode] = useState(false);
  const [showViewOnDropdown, setShowViewOnDropdown] = useState(false);
  const viewOnDropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewOnDropdownRef.current && !viewOnDropdownRef.current.contains(event.target)) {
        setShowViewOnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Keep cache helpers behind refs so effect callbacks stay stable when cache updates.
  const getFromCacheRef = useRef(getFromCache);
  const addToCacheRef = useRef(addToCache);
  useEffect(() => {
    getFromCacheRef.current = getFromCache;
    addToCacheRef.current = addToCache;
  }, [getFromCache, addToCache]);

  const updateOptimisticComment = useCallback((permlink, updates) => {
    setComments(prev => updateInTree(prev, permlink, updates));
  }, []);

  const insertOptimisticReply = (nodes, parentAuthor, parentPermlink, newComment) =>
    nodes.map(node => {
      if (node.author === parentAuthor && node.permlink === parentPermlink) {
        return { ...node, replies: [newComment, ...(node.replies || [])] };
      }
      if (node.replies?.length) {
        return { ...node, replies: insertOptimisticReply(node.replies, parentAuthor, parentPermlink, newComment) };
      }
      return node;
    });

  const scheduleCommentConfirmationCheck = useCallback((author, permlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(author, permlink);
        if (exists) {
          updateOptimisticComment(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          return;
        }
        if (index === checkDelays.length - 1) {
          updateOptimisticComment(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
        }
      }, delay);
    });
  }, [updateOptimisticComment]);

  const returnPath = sessionStorage.getItem('returnPath') || '/';

  const handleBackClick = (e) => {
    e.preventDefault();
    sessionStorage.setItem('preserveScroll', 'true');
    navigate(returnPath);
  };

  // Calculate reading time (avg 200 words per minute)
  const calculateReadingTime = (text) => {
    const wordsPerMinute = 200;
    const wordCount = (text || '').split(/\s+/).filter(w => w.length > 0).length;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  };

  const loadParentIfNeeded = useCallback((currentPost) => {
    const pAuth = currentPost.parent_author || currentPost.parentAuthor;
    const pPerm = currentPost.parent_permlink || currentPost.parentPermlink;
    const normalizedParentPermlink = String(pPerm || '').trim().toLowerCase();
    const isPlaceholderPermlink = normalizedParentPermlink === 'none' || normalizedParentPermlink === 'null';
    if (pAuth && pPerm) {
      if (isPlaceholderPermlink) {
        setParentPost(null);
        return;
      }
      const cachedParent = getFromCacheRef.current(pAuth, pPerm);
      if (cachedParent?.post) {
        setParentPost(cachedParent.post);
      } else {
        postsApi.getPost(pAuth, pPerm, false)
          .then(res => {
            if (res.post) {
              setParentPost(res.post);
              addToCacheRef.current(pAuth, pPerm, { post: res.post, comments: [], commentsLoaded: false });
            }
          })
          .catch(() => {});
      }
    } else {
      setParentPost(null);
    }
  }, []);

  const fetchPost = useCallback(async () => {
    let hydratedFromCache = false;
    setLoading(true);
    setError('');
    try {
      const cached = getFromCacheRef.current(author, permlink);
      if (cached?.post) {
        setPost(cached.post);
        loadParentIfNeeded(cached.post);
        setReadingTime(calculateReadingTime(cached.post.body));
        const commentTree = Array.isArray(cached.nestedComments)
          ? normalizeNestedComments(cached.nestedComments, author, permlink)
          : buildCommentTree(cached.comments || [], author, permlink);
        setComments(normalizeNestedComments(commentTree, author, permlink));
        hydratedFromCache = true;
        setLoading(false);
      }

      const postData = await postsApi.getPost(author, permlink, false);
      setPost(postData.post);
      loadParentIfNeeded(postData.post);
      setReadingTime(calculateReadingTime(postData.post.body));
      if (!hydratedFromCache) {
        setComments([]);
        setLoading(false);
      }
      addToCacheRef.current(author, permlink, { post: postData.post, comments: [], commentsLoaded: false });

      const commentData = await postsApi.getPostAll(author, permlink, 'live');
      const nestedComments = normalizeNestedComments(
        Array.isArray(commentData.comments) ? commentData.comments : [],
        author,
        permlink
      );
      const flatComments = flattenCommentTree(nestedComments);
      setComments(nestedComments);
      addToCacheRef.current(author, permlink, {
        post: postData.post,
        comments: flatComments,
        nestedComments,
        commentsLoaded: true
      });
    } catch (err) {
      console.error('Error fetching post:', err);
      console.error('Error details:', err.response?.data || err.message);
      if (!hydratedFromCache) {
        setError(`Failed to load post: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [author, permlink, loadParentIfNeeded]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);



  const handleCommentPosted = () => {
    fetchPost();
  };

  const handleOptimisticComment = (optimisticComment) => {
    setComments(prev => [optimisticComment, ...prev]);
    if (post && typeof post.children === 'number') {
      setPost(p => ({ ...p, children: p.children + 1 }));
    }
    scheduleCommentConfirmationCheck(optimisticComment.author, optimisticComment.permlink);
  };

  const handleOptimisticReply = (parentAuthor, parentPermlink, optimisticComment) => {
    setComments(prev => insertOptimisticReply(prev, parentAuthor, parentPermlink, optimisticComment));
    if (post && typeof post.children === 'number') {
      setPost(p => ({ ...p, children: p.children + 1 }));
    }
    scheduleCommentConfirmationCheck(optimisticComment.author, optimisticComment.permlink);
  };

  // when the comments array is updated (including on initial load from the
  // server) look for any optimistic comments stuck in "pending" state and
  // schedule the confirmation timers again.  without this the item would
  // remain pending forever after a page reload.
  useEffect(() => {
    comments.forEach(comment => {
      if (comment.optimistic && comment.optimisticStatus === 'pending') {
        scheduleCommentConfirmationCheck(comment.author, comment.permlink);
      }
    });
  }, [comments, scheduleCommentConfirmationCheck]);

  const handleRetryOptimisticComment = async (comment) => {
    if (!comment.parentAuthor || !comment.parentPermlink) return;
    try {
      updateOptimisticComment(comment.permlink, { optimisticStatus: 'pending', optimisticError: '' });
      await hiveService.createComment(
        comment.author,
        comment.parentAuthor,
        comment.parentPermlink,
        comment.body,
        comment.rootAuthor || comment.parentAuthor,
        { permlink: comment.permlink }
      );
      await postsApi.upsertOptimisticShortPost(buildOptimisticCommentUpsertPayload({
        author: comment.author,
        permlink: comment.permlink,
        body: comment.body,
        jsonMetadata: comment.json_metadata,
        targetPost: comment.parentAuthor && comment.parentPermlink
          ? { author: comment.parentAuthor, permlink: comment.parentPermlink }
          : undefined,
        createdAt: comment.created,
        voteCount: comment.votes?.total || 0,
        replyCount: comment.replies || 0
      }));
      scheduleCommentConfirmationCheck(comment.author, comment.permlink);
    } catch (error) {
      updateOptimisticComment(comment.permlink, { optimisticStatus: 'failed', optimisticError: error.message || 'Retry failed' });
    }
  };

  const handleShare = () => {
    const text = `Check out this article: ${post.title}`;
    const url = `${window.location.origin}${buildPostPath(author, permlink)}`;

    if (navigator.share) {
      navigator.share({ title: post.title, text, url });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${post.title}\n${url}`);
      alert('Link copied to clipboard!');
    }
  };

  const handleScrollToComments = () => {
    commentsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReplyClick = () => {
    // Open the same reply editor used by the inline CommentsSection button
    openEditor(replyEditorId);

    // Scroll editor into view and focus it once rendered
    setTimeout(() => {
      if (commentsRef?.current?.scrollIntoView) {
        commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (replyBoxRef?.current?.focus) {
        replyBoxRef.current.focus();
      }
    }, 100);
  };

  const toggleDistractionlessMode = () => {
    setIsDistractionlessMode(prev => !prev);
    // Add/remove class to body for global styling
    document.body.classList.toggle('distraction-less-reading');
  };

  // Reset distraction-less mode on page change
  useEffect(() => {
    return () => {
      setIsDistractionlessMode(false);
      document.body.classList.remove('distraction-less-reading');
    };
  }, [author, permlink]);

  // Compute authoritative comments count for header (prefer on-chain `post.children`, fallback to nested comment state)
  const countNested = (arr) => (arr || []).reduce((acc, c) => acc + 1 + (c.replies ? countNested(c.replies) : 0), 0);
  const totalCommentsCount = typeof post?.children === 'number' ? post.children : countNested(comments || []);

  const metadata = useMemo(() => {
    const rawMetadata = post?.json_metadata ?? post?.metadata ?? post?.jsonMetadata ?? null;
    if (!rawMetadata) return null;
    if (typeof rawMetadata === 'string') {
      try {
        return JSON.parse(rawMetadata);
      } catch (error) {
        return null;
      }
    }
    return typeof rawMetadata === 'object' ? rawMetadata : null;
  }, [post]);

  const appInfo = useMemo(() => getPostAppInfo(post, metadata), [post, metadata]);

  const tags = useMemo(() => (Array.isArray(metadata?.tags) ? metadata.tags : []), [metadata]);
  const detailImages = useMemo(() => {
    if (!metadata) return [];
    if (Array.isArray(metadata.image) && metadata.image.length > 0) return metadata.image;
    if (Array.isArray(metadata.images) && metadata.images.length > 0) return metadata.images;
    if (typeof metadata.thumbnail === 'string' && metadata.thumbnail.trim()) return [metadata.thumbnail.trim()];
    return [];
  }, [metadata]);
  const communitySlug = useMemo(() => {
    const hiveTag = tags.find((tag) => typeof tag === 'string' && tag.startsWith('hive-'));
    if (hiveTag) {
      return hiveTag;
    }
    if (post?.category && typeof post.category === 'string' && post.category.startsWith('hive-')) {
      return post.category;
    }
    return null;
  }, [tags, post?.category]);

  const [remoteCommunityTitle, setRemoteCommunityTitle] = useState(null);
  useEffect(() => {
    if (communitySlug && !post?.communityTitle) {
      hiveService.getCommunityTitle(communitySlug)
        .then((title) => {
          if (title) {
            setRemoteCommunityTitle(title);
          }
        })
        .catch(() => null);
      return;
    }
    setRemoteCommunityTitle(null);
  }, [communitySlug, post?.communityTitle]);

  const communityTitle = post?.communityTitle || remoteCommunityTitle || communitySlug;

  const parentPreviewText = useMemo(() => {
    if (!parentPost?.body) return 'View parent comment...';
    const clean = parentPost.body.replace(/!\[.*?\]\(.*?\)/g, '').replace(/<[^>]+>/g, '').trim();
    if (!clean) return 'View parent comment...';
    // Matches up to 180 chars, plus the rest of the current word
    const match = clean.match(/^(.{0,180}\S*)/s);
    const preview = match ? match[1] : clean;
    return preview.length < clean.length ? preview + '...' : preview;
  }, [parentPost]);

  if (loading) {
    return (
      <div className="post-detail-container">
        <SidebarHeader title="Post Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <LoadingSpinner text="Loading article..." />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="post-detail-container">
        <SidebarHeader title="Post Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="error-box">
          <h2>⚠️ Article Not Found</h2>
          <p>{error || 'This article could not be loaded.'}</p>
          <Link to={returnPath} onClick={handleBackClick} className="back-link">
            ← Back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SidebarHeader title="Post Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <ReadingProgressBar targetRef={articleRef} />
      
      <div className={`post-detail-container ${isDistractionlessMode ? 'distraction-less' : ''}`} ref={articleRef}>
        {/* Distraction-less Mode Toggle */}
        <button 
          className={`distraction-less-toggle desktop-only ${isDistractionlessMode ? 'active' : ''}`}
          onClick={toggleDistractionlessMode}
          title={isDistractionlessMode ? 'Exit distraction-less mode' : 'Enter distraction-less mode'}
        >
          {isDistractionlessMode ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="9" />
              <rect x="14" y="3" width="7" height="5" />
              <rect x="14" y="12" width="7" height="9" />
              <rect x="3" y="16" width="7" height="5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>

        {/* Back button */}
        <div className="article-back-nav">
          <Link to={returnPath} onClick={handleBackClick} className="back-link">
            ← Back
          </Link>
        </div>

        {/* Main article layout */}
        <div className="article-wrapper">
          {/* Main content */}
          <main className="article-main">
            {/* Article Header */}
            <ArticleHeader 
              post={post}
              readingTime={readingTime}
              commentsCount={totalCommentsCount}
              onShare={handleShare}
              onScrollToComments={handleScrollToComments}
              onReplyClick={handleReplyClick}
            />

            {/* Parent Post Preview */}
            {parentPost && (
              <div className="parent-post-preview" onClick={() => navigate(buildContentPath(parentPost.author, parentPost.permlink, parentPost))}>
                <div className="parent-post-preview-header">
                  Replying to <Link to={`/user/${parentPost.author}`} onClick={e => e.stopPropagation()} className="parent-post-preview-author">@{parentPost.author}</Link>:
                </div>
                <Link to={buildContentPath(parentPost.author, parentPost.permlink, parentPost)} className="parent-post-preview-link">
                  <div className="parent-body-preview">
                    {parentPreviewText}
                  </div>
                </Link>
              </div>
            )}

            {/* Article Content */}
            <ArticleContent
              content={post.body}
              images={detailImages}
              forceInlineImages={true}
              enableContiguousImageGrid={true}
              title={post.title}
              author={post.author}
              permlink={post.permlink}
              isArticle={!post.parentAuthor && post.depth === 0}
            />

            

            {/* Footer with engagement */}
            <div className="article-footer">

              {(appInfo || communityTitle) && (
                <div className="article-footer-meta">
                  {appInfo && (
                    <span className="article-footer-via-app" title={appInfo.key}>
                      Created via <em>{appInfo.name}</em>
                    </span>
                  )}
                  {metadata?.sourced && (
                    <span className="article-footer-sourced">
                      Sourced in <em>{metadata.sourced}</em>
                    </span>
                  )}
                </div>
              )}

              {/* Tags (repeat below content, same style as header) */}
              <h4 className="tag-title">Tags</h4>
              {(tags.length > 0 || (post.parentAuthor && post.parentPermlink)) && (
                <div className="article-tags" style={{ marginTop: 18 }}>
                  {communityTitle && communitySlug && (
                    <Link to={`/c/${communitySlug}`} className="article-tag article-community-title article-footer-community-title">
                      {communityTitle}
                    </Link>
                  )}
                  {post.parentAuthor && post.parentPermlink && (
                    <Link to={buildPostPath(post.parentAuthor, post.parentPermlink)} className="article-tag article-community-tag">
                      {post.parentAuthor}/{post.parentPermlink}
                    </Link>
                  )}
                  {tags.map((tag, i) => (
                    <Link key={i} to={`/tag/${tag}`} className="article-tag">#{tag}</Link>
                  ))}
                </div>
              )}

              <ArticleActions 
                post={post}
                onReplyClick={handleReplyClick}
                showReply={true}
              />
              
              <div className="article-actions">
                <button className="article-action-btn" onClick={handleShare} title="Share this article">
                  📤 Share
                </button>
                <div className="view-on-dropdown-container" ref={viewOnDropdownRef}>
                  <button 
                    className="article-action-btn" 
                    onClick={() => setShowViewOnDropdown(!showViewOnDropdown)}
                  >
                    🔗 View on
                  </button>
                  {showViewOnDropdown && (
                    <div className="view-on-dropdown">
                      {HIVE_FRONTENDS.map((frontend) => (
                        <a
                          key={frontend.id}
                          href={frontend.getUrl(author, permlink)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-on-link"
                        >
                          {frontend.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {user?.username?.toLowerCase() === post?.author?.toLowerCase() &&
                  post?.created &&
                  (Date.now() - new Date(post.created).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                  <button
                    className="article-action-btn"
                    onClick={() => navigate(`/create-article?edit=true&author=${encodeURIComponent(post.author)}&permlink=${encodeURIComponent(post.permlink)}`)}
                    title="Edit this article"
                  >
                    ✏️ Edit
                  </button>
                )}
              </div>
            </div>

            {/* Comments Section */}
            <CommentsSection
              ref={commentsRef}
              replyBoxRef={replyBoxRef}
              comments={comments}
              loading={false}
              post={post}
              onCommentPosted={handleCommentPosted}
              onOptimisticComment={handleOptimisticComment}
              onOptimisticReply={handleOptimisticReply}
              onOptimisticCommentUpdate={updateOptimisticComment}
              onRetryOptimistic={handleRetryOptimisticComment}
            />
          </main>
        </div>

        {/* Navigation buttons */}
        <ArticleNavButtons 
          articleRef={articleRef}
          commentsRef={commentsRef}
        />
      </div>
    </>
  );
};

export default PostDetail;
